// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - PLAN MARAÎCHAGE V6.2 (CORRIGÉ)
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy / Joshua - Janvier 2026
//
// Connecté aux vraies tables :
// - zones_ferme (positions, types, modules)
// - simulation_etats (mesures temps réel)
// - simulation_equipements (vannes, pompes, etc.)
// - v_simulation_liens_eau (liens hydrauliques)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Map, Thermometer, Droplets, Snowflake,
    RefreshCw, Save, Lock, Unlock, Play, Pause,
    Wifi, X, Power, Settings, Sprout, Zap, Activity, Wind
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
const TYPES_ZONES = {
    vegetation: { label: 'Végétation', icon: '🌱', bgColor: 'bg-green-50', borderColor: 'border-green-400', headerBg: 'bg-green-500' },
    stockage: { label: 'Stockage', icon: '❄️', bgColor: 'bg-cyan-50', borderColor: 'border-cyan-400', headerBg: 'bg-cyan-500' },
    technique: { label: 'Technique', icon: '⚙️', bgColor: 'bg-amber-50', borderColor: 'border-amber-400', headerBg: 'bg-amber-500' },
    meteo: { label: 'Météo', icon: '🌤️', bgColor: 'bg-purple-50', borderColor: 'border-purple-400', headerBg: 'bg-purple-500' }
};

const TYPES_EQUIPEMENTS = {
    vanne: { icon: '💧', label: 'Vanne', color: '#3b82f6' },
    pompe: { icon: '⚡', label: 'Pompe', color: '#f59e0b' },
    brumisation: { icon: '🌫️', label: 'Brumisation', color: '#06b6d4' },
    ventilation: { icon: '💨', label: 'Ventilation', color: '#6b7280' },
    chauffage: { icon: '🔥', label: 'Chauffage', color: '#ef4444' },
    refroidissement: { icon: '❄️', label: 'Refroidissement', color: '#0ea5e9' },
    eclairage: { icon: '💡', label: 'Éclairage', color: '#eab308' },
    brasseur: { icon: '🌀', label: 'Brasseur', color: '#22c55e' },
    voile: { icon: '🛡️', label: 'Voile', color: '#a855f7' },
    goutte_a_goutte: { icon: '🚿', label: 'Goutte-à-goutte', color: '#0ea5e9' }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
const PlanMaraichage = () => {
    // États données
    const [zones, setZones] = useState([]);
    const [etatsSimules, setEtatsSimules] = useState({});  // Par module_code
    const [equipements, setEquipements] = useState([]);
    const [equipementsActifs, setEquipementsActifs] = useState({});  // Par module_id
    const [liensEau, setLiensEau] = useState([]);
    const [simulationStatus, setSimulationStatus] = useState({ running: false, tickCount: 0 });
    
    // États UI
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);
    const [selectedEquipement, setSelectedEquipement] = useState(null);
    const [showSupervisionModal, setShowSupervisionModal] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [dragState, setDragState] = useState(null);
    
    const canvasRef = useRef(null);

    // ═══════════════════════════════════════════════════════════════════════
    // CHARGEMENT DONNÉES
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        loadAllData();
        const interval = setInterval(loadSimulationData, 3000);
        return () => clearInterval(interval);
    }, []);

    const loadAllData = async () => {
        setLoading(true);
        try {
            await loadZones();
            await loadSimulationData();
        } catch (err) {
            console.error('Erreur chargement:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadZones = async () => {
        try {
            const res = await fetch(`${API_BASE}/zones-ferme`);
            const data = await res.json();
            if (data.success) {
                setZones(data.data || []);
            }
        } catch (err) {
            console.error('Erreur zones:', err);
        }
    };

    const loadSimulationData = async () => {
        try {
            // Status simulation
            const statusRes = await fetch(`${API_BASE}/simulation/status`);
            const statusData = await statusRes.json();
            if (statusData.success) setSimulationStatus(statusData.data);

            // États simulés (depuis v_simulation_dashboard)
            const etatsRes = await fetch(`${API_BASE}/simulation/etats`);
            const etatsData = await etatsRes.json();
            if (etatsData.success) {
                // Indexer par code_module pour faciliter l'accès
                const etatsMap = {};
                etatsData.data.forEach(e => {
                    if (e.code_module) etatsMap[e.code_module] = e;
                });
                setEtatsSimules(etatsMap);
            }

            // Équipements
            const eqRes = await fetch(`${API_BASE}/simulation/equipements`);
            const eqData = await eqRes.json();
            if (eqData.success) setEquipements(eqData.data || []);

            // Équipements actifs (groupés par module_id)
            const eqActifsRes = await fetch(`${API_BASE}/simulation/equipements/actifs`);
            const eqActifsData = await eqActifsRes.json();
            if (eqActifsData.success) setEquipementsActifs(eqActifsData.byModule || {});

            // Liens eau actifs
            const liensRes = await fetch(`${API_BASE}/simulation/liens-eau`);
            const liensData = await liensRes.json();
            if (liensData.success) setLiensEau(liensData.data || []);

            setLastUpdate(new Date());
        } catch (err) {
            console.error('Erreur simulation data:', err);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // CALCUL LIENS SVG DYNAMIQUES
    // ═══════════════════════════════════════════════════════════════════════
    const liensIrrigation = useMemo(() => {
        // Les liens viennent directement avec les positions depuis v_simulation_liens_eau
        return liensEau.map(lien => {
            // Trouver les zones pour avoir les positions à jour (si déplacées)
            const sourceZone = zones.find(z => z.module_code === lien.source_code);
            const destZone = zones.find(z => z.module_code === lien.dest_code);
            
            if (!sourceZone || !destZone) return null;
            
            return {
                ...lien,
                source_x: sourceZone.position_x,
                source_y: sourceZone.position_y,
                source_w: sourceZone.largeur,
                source_h: sourceZone.hauteur,
                dest_x: destZone.position_x,
                dest_y: destZone.position_y,
                dest_w: destZone.largeur,
                dest_h: destZone.hauteur
            };
        }).filter(Boolean);
    }, [liensEau, zones]);

    // ═══════════════════════════════════════════════════════════════════════
    // CONTRÔLE SIMULATION
    // ═══════════════════════════════════════════════════════════════════════
    const toggleSimulation = async () => {
        const endpoint = simulationStatus.running ? 'stop' : 'start';
        await fetch(`${API_BASE}/simulation/${endpoint}`, { method: 'POST' });
        await loadSimulationData();
    };

    const resetSimulation = async () => {
        if (!window.confirm('Réinitialiser tous les états simulés ?')) return;
        await fetch(`${API_BASE}/simulation/reset`, { method: 'POST' });
        await loadSimulationData();
    };

    // ═══════════════════════════════════════════════════════════════════════
    // ACTIONS ÉQUIPEMENTS (avec chaînage automatique)
    // ═══════════════════════════════════════════════════════════════════════
    const toggleEquipement = async (equipement, newEtat) => {
        try {
            const res = await fetch(`${API_BASE}/simulation/equipements/${equipement.id}/etat`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ etat: newEtat, source: 'plan_maraichage' })
            });
            const data = await res.json();
            if (data.success) {
                await loadSimulationData();
                // Mettre à jour l'équipement sélectionné dans le modal
                setSelectedEquipement(prev => prev ? { ...prev, etat: newEtat } : null);
            }
        } catch (err) {
            console.error('Erreur toggle équipement:', err);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // DRAG & RESIZE
    // ═══════════════════════════════════════════════════════════════════════
    const handleMouseDown = useCallback((e, zone, corner = null) => {
        if (!editMode) return;
        e.preventDefault();
        e.stopPropagation();
        
        setDragState({
            zoneId: zone.id,
            corner,
            startX: e.clientX,
            startY: e.clientY,
            startZoneX: zone.position_x,
            startZoneY: zone.position_y,
            startZoneW: zone.largeur,
            startZoneH: zone.hauteur
        });
    }, [editMode]);

    const handleMouseMove = useCallback((e) => {
        if (!dragState) return;
        
        const deltaX = e.clientX - dragState.startX;
        const deltaY = e.clientY - dragState.startY;
        
        setZones(prevZones => prevZones.map(zone => {
            if (zone.id !== dragState.zoneId) return zone;
            
            let newX = zone.position_x, newY = zone.position_y;
            let newW = zone.largeur, newH = zone.hauteur;
            
            if (!dragState.corner) {
                // Déplacement
                newX = Math.max(0, dragState.startZoneX + deltaX);
                newY = Math.max(0, dragState.startZoneY + deltaY);
            } else {
                // Resize
                const corner = dragState.corner;
                if (corner.includes('e')) newW = Math.max(80, dragState.startZoneW + deltaX);
                if (corner.includes('w')) { newW = Math.max(80, dragState.startZoneW - deltaX); newX = dragState.startZoneX + (dragState.startZoneW - newW); }
                if (corner.includes('s')) newH = Math.max(60, dragState.startZoneH + deltaY);
                if (corner.includes('n')) { newH = Math.max(60, dragState.startZoneH - deltaY); newY = dragState.startZoneY + (dragState.startZoneH - newH); }
            }
            
            return { ...zone, position_x: Math.round(newX), position_y: Math.round(newY), largeur: Math.round(newW), hauteur: Math.round(newH) };
        }));
        setHasChanges(true);
    }, [dragState]);

    const handleMouseUp = useCallback(() => setDragState(null), []);

    useEffect(() => {
        if (dragState) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [dragState, handleMouseMove, handleMouseUp]);

    // ═══════════════════════════════════════════════════════════════════════
    // SAUVEGARDE POSITIONS
    // ═══════════════════════════════════════════════════════════════════════
    const saveAllChanges = async () => {
        try {
            const res = await fetch(`${API_BASE}/zones-ferme/positions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    zones: zones.map(z => ({ 
                        id: z.id, 
                        position_x: z.position_x, 
                        position_y: z.position_y, 
                        largeur: z.largeur, 
                        hauteur: z.hauteur 
                    })) 
                })
            });
            const data = await res.json();
            if (data.success) {
                setHasChanges(false);
                alert('✅ Positions sauvegardées !');
            }
        } catch (err) { 
            console.error('Erreur sauvegarde:', err); 
            alert('❌ Erreur sauvegarde');
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // HELPERS
    // ═══════════════════════════════════════════════════════════════════════
    const getEtatByModuleCode = (moduleCode) => {
        return etatsSimules[moduleCode] || null;
    };

    const getEquipementsByModuleCode = (moduleCode) => {
        return equipements.filter(e => e.code_module === moduleCode);
    };

    const getEquipementsActifsByModuleCode = (moduleCode) => {
        const etat = etatsSimules[moduleCode];
        if (!etat) return [];
        return equipementsActifs[etat.module_id] || [];
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU LIENS SVG
    // ═══════════════════════════════════════════════════════════════════════
    const renderLiensSVG = () => {
        if (liensIrrigation.length === 0) return null;

        return (
            <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%', zIndex: 5 }}>
                <defs>
                    <linearGradient id="waterFlow" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
                        <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.6" />
                    </linearGradient>
                    <marker id="arrowHead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
                    </marker>
                </defs>

                {liensIrrigation.map((lien, index) => {
                    const sourceX = lien.source_x + lien.source_w / 2;
                    const sourceY = lien.source_y + lien.source_h / 2;
                    const destX = lien.dest_x + lien.dest_w / 2;
                    const destY = lien.dest_y + lien.dest_h / 2;
                    const midX = (sourceX + destX) / 2;
                    const midY = Math.min(sourceY, destY) - 30;

                    return (
                        <g key={`lien-${index}`}>
                            {/* Ombre */}
                            <path 
                                d={`M ${sourceX} ${sourceY} Q ${midX} ${midY} ${destX} ${destY}`} 
                                fill="none" 
                                stroke="rgba(59, 130, 246, 0.2)" 
                                strokeWidth="10" 
                                strokeLinecap="round" 
                            />
                            {/* Ligne principale animée */}
                            <path 
                                d={`M ${sourceX} ${sourceY} Q ${midX} ${midY} ${destX} ${destY}`} 
                                fill="none" 
                                stroke="url(#waterFlow)" 
                                strokeWidth="4" 
                                strokeLinecap="round" 
                                strokeDasharray="12,6" 
                                markerEnd="url(#arrowHead)"
                            >
                                <animate attributeName="stroke-dashoffset" from="0" to="-36" dur="1.5s" repeatCount="indefinite" />
                            </path>
                            {/* Icône et débit */}
                            <text x={midX} y={midY + 5} textAnchor="middle" fontSize="14">💧</text>
                            <text x={midX} y={midY + 18} textAnchor="middle" fontSize="9" fill="#3b82f6" fontWeight="bold">
                                {lien.debit_nominal} L/min
                            </text>
                        </g>
                    );
                })}
            </svg>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU ICÔNES ÉQUIPEMENTS SUR ZONE
    // ═══════════════════════════════════════════════════════════════════════
    const renderEquipementsIcons = (zone) => {
        const eqActifs = getEquipementsActifsByModuleCode(zone.module_code);
        if (eqActifs.length === 0) return null;

        return (
            <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5">
                {eqActifs.map((eq) => {
                    const config = TYPES_EQUIPEMENTS[eq.type_equipement] || {};
                    const isOn = eq.etat === 'on';
                    
                    return (
                        <button
                            key={eq.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEquipement(eq);
                                setShowSupervisionModal(true);
                            }}
                            className={`w-6 h-6 rounded-full flex items-center justify-center transition-all transform hover:scale-125 cursor-pointer ${isOn ? 'animate-pulse' : ''}`}
                            style={{ 
                                backgroundColor: isOn ? config.color : '#9ca3af', 
                                boxShadow: isOn ? `0 0 8px ${config.color}` : 'none' 
                            }}
                            title={`${eq.nom} - ${eq.etat.toUpperCase()}`}
                        >
                            <span className="text-white text-xs">{config.icon}</span>
                        </button>
                    );
                })}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU CONTENU ZONE (données simulées)
    // ═══════════════════════════════════════════════════════════════════════
    const renderZoneContent = (zone) => {
        const etat = getEtatByModuleCode(zone.module_code);

        // Végétation
        if (zone.type_zone === 'vegetation') {
            return (
                <div className="p-1.5 text-xs">
                    {etat ? (
                        <div className="space-y-0.5">
                            <div className="flex items-center gap-1">
                                <Thermometer className="w-3 h-3 text-red-500" />
                                <span className={etat.temperature < 10 ? 'text-blue-600 font-bold' : ''}>{parseFloat(etat.temperature || 0).toFixed(1)}°C</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Droplets className="w-3 h-3 text-blue-500" />
                                {parseFloat(etat.humidity || 0).toFixed(0)}%
                            </div>
                            {etat.humidity_sol !== null && (
                                <div className="flex items-center gap-1 text-amber-600">
                                    <span>💧</span>
                                    Sol: {parseFloat(etat.humidity_sol || 0).toFixed(0)}%
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <Sprout className="w-5 h-5 opacity-40" />
                            <span className="text-[10px]">Pas de données</span>
                        </div>
                    )}
                </div>
            );
        }

        // Stockage
        if (zone.type_zone === 'stockage' && etat) {
            return (
                <div className="p-1.5 text-xs space-y-0.5">
                    <div className="flex items-center gap-1">
                        <Thermometer className="w-3 h-3" />
                        <span className={etat.temperature < 6 ? 'text-blue-600 font-bold' : 'text-red-500'}>
                            {parseFloat(etat.temperature || 0).toFixed(1)}°C
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Droplets className="w-3 h-3 text-blue-500" />
                        {parseFloat(etat.humidity || 0).toFixed(0)}%
                    </div>
                    {etat.point_rosee !== null && (
                        <div className="flex items-center gap-1 text-cyan-600 text-[10px]">
                            <Snowflake className="w-3 h-3" />
                            Rosée {parseFloat(etat.point_rosee || 0).toFixed(1)}°C
                        </div>
                    )}
                </div>
            );
        }

        // Technique (Tank, Compost)
        if (zone.type_zone === 'technique' && etat) {
            // Tank
            if (etat.niveau_eau !== null) {
                return (
                    <div className="p-1.5 space-y-1">
                        <div className="text-center">
                            <span className="text-xl font-bold text-blue-600">{parseFloat(etat.volume_m3 || 0).toFixed(2)}</span>
                            <span className="text-xs text-gray-500"> m³</span>
                        </div>
                        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                                className={`absolute left-0 top-0 h-full transition-all duration-500 ${
                                    etat.niveau_eau > 60 ? 'bg-blue-500' : 
                                    etat.niveau_eau > 30 ? 'bg-yellow-500' : 'bg-red-500'
                                }`} 
                                style={{ width: `${Math.min(100, etat.niveau_eau)}%` }} 
                            />
                        </div>
                        <div className="text-center text-xs text-gray-500">{parseFloat(etat.niveau_eau || 0).toFixed(0)}%</div>
                    </div>
                );
            }
            // Compost
            if (etat.temp_compost !== null) {
                return (
                    <div className="p-1.5 text-center">
                        <div className="font-semibold text-amber-700 text-sm">
                            {etat.temp_compost >= 45 ? '🔥 Actif' : '💤 Lent'}
                        </div>
                        <div className="text-xl font-bold">{parseFloat(etat.temp_compost || 0).toFixed(0)}°C</div>
                    </div>
                );
            }
        }

        // Météo
        if (zone.type_zone === 'meteo' && etat) {
            return (
                <div className="p-1.5 text-xs space-y-0.5">
                    <div className="flex items-center gap-1">
                        <Thermometer className="w-3 h-3" />
                        {parseFloat(etat.temperature || 0).toFixed(1)}°C
                    </div>
                    <div className="flex items-center gap-1">
                        <Droplets className="w-3 h-3" />
                        {parseFloat(etat.humidity || 0).toFixed(0)}%
                    </div>
                    {etat.vent_vitesse !== null && (
                        <div className="flex items-center gap-1">
                            <Wind className="w-3 h-3" />
                            {parseFloat(etat.vent_vitesse || 0).toFixed(0)} km/h
                        </div>
                    )}
                </div>
            );
        }

        return <div className="text-xs text-gray-400 p-1 text-center">--</div>;
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU POIGNÉES RESIZE
    // ═══════════════════════════════════════════════════════════════════════
    const renderResizeHandles = (zone) => {
        if (!editMode) return null;

        const handles = [
            { corner: 'nw', position: '-top-1.5 -left-1.5', cursor: 'nw-resize' },
            { corner: 'n', position: '-top-1.5 left-1/2 -translate-x-1/2', cursor: 'n-resize' },
            { corner: 'ne', position: '-top-1.5 -right-1.5', cursor: 'ne-resize' },
            { corner: 'w', position: 'top-1/2 -left-1.5 -translate-y-1/2', cursor: 'w-resize' },
            { corner: 'e', position: 'top-1/2 -right-1.5 -translate-y-1/2', cursor: 'e-resize' },
            { corner: 'sw', position: '-bottom-1.5 -left-1.5', cursor: 'sw-resize' },
            { corner: 's', position: '-bottom-1.5 left-1/2 -translate-x-1/2', cursor: 's-resize' },
            { corner: 'se', position: '-bottom-1.5 -right-1.5', cursor: 'se-resize' }
        ];

        return handles.map(({ corner, position, cursor }) => (
            <div
                key={corner}
                onMouseDown={(e) => handleMouseDown(e, zone, corner)}
                className={`absolute ${position} w-3 h-3 bg-blue-500 hover:bg-blue-600 rounded-full z-50 border-2 border-white shadow-md`}
                style={{ cursor }}
            />
        ));
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU ZONE
    // ═══════════════════════════════════════════════════════════════════════
    const renderZone = (zone) => {
        const typeConfig = TYPES_ZONES[zone.type_zone] || TYPES_ZONES.vegetation;
        const isSelected = selectedZone?.id === zone.id;
        const isDragging = dragState?.zoneId === zone.id && !dragState.corner;
        const eqActifs = getEquipementsActifsByModuleCode(zone.module_code);
        const hasEquipActifs = eqActifs.length > 0;

        return (
            <div
                key={zone.id}
                onMouseDown={(e) => handleMouseDown(e, zone, null)}
                onClick={(e) => { 
                    e.stopPropagation(); 
                    if (!dragState) setSelectedZone(isSelected ? null : zone); 
                }}
                className={`
                    absolute rounded-lg border-2 shadow-md transition-all overflow-visible
                    ${typeConfig.bgColor} ${typeConfig.borderColor}
                    ${isSelected ? 'ring-4 ring-blue-400 z-30' : 'z-10'}
                    ${isDragging ? 'opacity-90 shadow-xl z-40' : ''}
                    ${editMode ? 'cursor-move' : 'cursor-pointer hover:shadow-lg'}
                    ${hasEquipActifs ? 'ring-2 ring-green-400/60' : ''}
                `}
                style={{ 
                    left: zone.position_x, 
                    top: zone.position_y, 
                    width: zone.largeur, 
                    height: zone.hauteur 
                }}
            >
                {/* Header */}
                <div className={`${typeConfig.headerBg} text-white px-2 py-0.5 text-xs font-medium flex items-center justify-between rounded-t-md`}>
                    <span className="truncate">{zone.icone} {zone.nom}</span>
                    {zone.module_code && <Wifi className="w-3 h-3 flex-shrink-0 opacity-75" />}
                </div>

                {/* Contenu */}
                <div className="overflow-hidden" style={{ height: `calc(100% - 22px - 26px)` }}>
                    {renderZoneContent(zone)}
                </div>

                {/* Icônes équipements actifs */}
                {renderEquipementsIcons(zone)}

                {/* Poignées resize */}
                {renderResizeHandles(zone)}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // MODAL SUPERVISION ÉQUIPEMENT
    // ═══════════════════════════════════════════════════════════════════════
    const renderSupervisionModal = () => {
        if (!showSupervisionModal || !selectedEquipement) return null;

        const config = TYPES_EQUIPEMENTS[selectedEquipement.type_equipement] || {};
        const isOn = selectedEquipement.etat === 'on';
        const isAuto = selectedEquipement.etat === 'auto';
        const moduleEquipements = equipements.filter(e => e.module_id === selectedEquipement.module_id);
        const etat = Object.values(etatsSimules).find(e => e.module_id === selectedEquipement.module_id);

        return (
            <div 
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" 
                onClick={() => setShowSupervisionModal(false)}
            >
                <div 
                    className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" 
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-4" style={{ backgroundColor: config.color }}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-3xl">{config.icon}</span>
                                <div>
                                    <h2 className="text-lg font-bold text-white">{selectedEquipement.nom}</h2>
                                    <p className="text-white/80 text-sm">{selectedEquipement.zone_nom || selectedEquipement.module_nom}</p>
                                </div>
                            </div>
                            <button onClick={() => setShowSupervisionModal(false)} className="text-white/80 hover:text-white">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* État actuel */}
                    <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full ${isOn ? 'bg-green-500 animate-pulse' : isAuto ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                            <span className="font-medium text-lg">
                                {isOn ? 'EN MARCHE' : isAuto ? 'AUTOMATIQUE' : 'ARRÊTÉ'}
                            </span>
                        </div>
                        {selectedEquipement.valeur > 0 && (
                            <span className="text-2xl font-bold text-gray-700">{selectedEquipement.valeur}%</span>
                        )}
                    </div>

                    {/* Mesures du module */}
                    {etat && (
                        <div className="p-3 bg-blue-50 border-b grid grid-cols-3 gap-2 text-center text-sm">
                            <div>
                                <div className="text-lg font-bold text-red-500">{parseFloat(etat.temperature || 0).toFixed(1)}°C</div>
                                <div className="text-xs text-gray-500">Température</div>
                            </div>
                            <div>
                                <div className="text-lg font-bold text-blue-500">{parseFloat(etat.humidity || 0).toFixed(0)}%</div>
                                <div className="text-xs text-gray-500">Humidité</div>
                            </div>
                            {etat.humidity_sol !== null && (
                                <div>
                                    <div className="text-lg font-bold text-amber-600">{parseFloat(etat.humidity_sol || 0).toFixed(0)}%</div>
                                    <div className="text-xs text-gray-500">Sol</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Boutons contrôle */}
                    <div className="p-4 grid grid-cols-3 gap-3">
                        <button
                            onClick={() => toggleEquipement(selectedEquipement, 'on')}
                            className={`p-3 rounded-lg font-medium transition-all ${isOn ? 'bg-green-500 text-white ring-2 ring-green-300' : 'bg-gray-100 hover:bg-green-100'}`}
                        >
                            <Power className="w-5 h-5 mx-auto mb-1" />
                            ON
                        </button>
                        <button
                            onClick={() => toggleEquipement(selectedEquipement, 'off')}
                            className={`p-3 rounded-lg font-medium transition-all ${!isOn && !isAuto ? 'bg-red-500 text-white ring-2 ring-red-300' : 'bg-gray-100 hover:bg-red-100'}`}
                        >
                            <Power className="w-5 h-5 mx-auto mb-1" />
                            OFF
                        </button>
                        <button
                            onClick={() => toggleEquipement(selectedEquipement, 'auto')}
                            className={`p-3 rounded-lg font-medium transition-all ${isAuto ? 'bg-yellow-500 text-white ring-2 ring-yellow-300' : 'bg-gray-100 hover:bg-yellow-100'}`}
                        >
                            <Settings className="w-5 h-5 mx-auto mb-1" />
                            AUTO
                        </button>
                    </div>

                    {/* Info chaînage */}
                    {selectedEquipement.source_module_id && (
                        <div className="px-4 pb-2 text-xs text-blue-600 bg-blue-50 py-2">
                            <Zap className="w-4 h-4 inline mr-1" />
                            <strong>Chaînage actif :</strong> Active automatiquement la pompe du tank
                        </div>
                    )}

                    {/* Autres équipements du module */}
                    {moduleEquipements.length > 1 && (
                        <div className="p-4 border-t">
                            <h3 className="font-medium text-gray-700 mb-2 text-sm">Autres équipements</h3>
                            <div className="flex flex-wrap gap-2">
                                {moduleEquipements.filter(e => e.id !== selectedEquipement.id).map(eq => {
                                    const eqConfig = TYPES_EQUIPEMENTS[eq.type_equipement] || {};
                                    return (
                                        <button
                                            key={eq.id}
                                            onClick={() => setSelectedEquipement(eq)}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-all 
                                                ${eq.etat === 'on' ? 'bg-green-100 border-green-300' : 'bg-gray-100 border-gray-200'} hover:shadow`}
                                        >
                                            <span>{eqConfig.icon}</span>
                                            <span className="truncate max-w-20">{eq.nom.split(' ').pop()}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Infos techniques */}
                    <div className="p-3 bg-gray-50 text-xs text-gray-500 flex justify-between border-t">
                        <span>Module: {selectedEquipement.code_module}</span>
                        <span>GPIO: {selectedEquipement.gpio_pin || '--'}</span>
                        <span>Débit: {selectedEquipement.debit_nominal || '--'} L/min</span>
                    </div>
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU PRINCIPAL
    // ═══════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-2 text-gray-600">Chargement Plan Maraîchage...</span>
            </div>
        );
    }

    const nbEquipActifs = Object.values(equipementsActifs).flat().length;
    const tankEtat = etatsSimules['ESP_GEST_01'];
    const meteoEtat = etatsSimules['ESP_MET_01'];

    return (
        <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Map className="w-6 h-6 text-green-600" />
                    Plan Maraîchage
                    {simulationStatus.running && (
                        <span className="ml-2 text-sm font-normal bg-green-100 text-green-700 px-2 py-0.5 rounded-full animate-pulse">
                            ● SIMULATION ACTIVE
                        </span>
                    )}
                </h1>
                
                <div className="flex items-center gap-2">
                    {/* Bouton Play/Pause simulation */}
                    <button
                        onClick={toggleSimulation}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            simulationStatus.running 
                                ? 'bg-red-500 text-white hover:bg-red-600' 
                                : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                    >
                        {simulationStatus.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        {simulationStatus.running ? 'Pause' : 'Simuler'}
                    </button>

                    {lastUpdate && <span className="text-xs text-gray-500">MAJ: {lastUpdate.toLocaleTimeString('fr-FR')}</span>}
                    
                    <button 
                        onClick={() => setEditMode(!editMode)} 
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            editMode ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                    >
                        {editMode ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        {editMode ? 'Édition' : 'Verrouillé'}
                    </button>
                    
                    {editMode && hasChanges && (
                        <button 
                            onClick={saveAllChanges} 
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm font-medium animate-pulse"
                        >
                            <Save className="w-4 h-4" /> Sauver
                        </button>
                    )}
                    
                    <button onClick={loadAllData} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-5 gap-3 mb-4">
                <div className="bg-green-100 border border-green-300 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-green-700">{zones.length}</div>
                    <div className="text-xs text-green-600">🗺️ Zones</div>
                </div>
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-2 text-center">
                    <div className={`text-xl font-bold ${nbEquipActifs > 0 ? 'text-blue-700 animate-pulse' : 'text-gray-400'}`}>
                        {nbEquipActifs}
                    </div>
                    <div className="text-xs text-blue-600">⚡ Équip. ON</div>
                </div>
                <div className="bg-cyan-100 border border-cyan-300 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-cyan-700">{liensIrrigation.length}</div>
                    <div className="text-xs text-cyan-600">💧 Irrigations</div>
                </div>
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-2 text-center">
                    <div className={`text-xl font-bold ${tankEtat && tankEtat.niveau_eau < 30 ? 'text-red-600' : 'text-amber-700'}`}>
                        {parseFloat(tankEtat?.niveau_eau || 0).toFixed(0) || '--'}%
                    </div>
                    <div className="text-xs text-amber-600">🛢️ Tank</div>
                </div>
                <div className="bg-purple-100 border border-purple-300 rounded-lg p-2 text-center">
                    <div className="text-xl font-bold text-purple-700">
                        {parseFloat(meteoEtat?.temperature || 0).toFixed(0) || '--'}°C
                    </div>
                    <div className="text-xs text-purple-600">🌤️ Météo</div>
                </div>
            </div>

            {/* Mode édition info */}
            {editMode && (
                <div className="mb-3 p-2 bg-orange-100 border border-orange-300 rounded-lg text-sm text-orange-800 flex items-center gap-2">
                    <span>🔧</span>
                    <span><strong>Mode édition</strong> — Déplacez et redimensionnez avec les poignées bleues</span>
                </div>
            )}

            {/* Canvas */}
            <div 
                ref={canvasRef}
                onClick={() => setSelectedZone(null)}
                className={`relative bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border-2 border-green-300 overflow-visible ${editMode ? 'ring-4 ring-orange-300' : ''}`}
                style={{ height: 580 }}
            >
                {/* Grille */}
                <div 
                    className="absolute inset-0 opacity-10 pointer-events-none" 
                    style={{ 
                        backgroundImage: 'linear-gradient(#22c55e 1px, transparent 1px), linear-gradient(90deg, #22c55e 1px, transparent 1px)', 
                        backgroundSize: '50px 50px' 
                    }} 
                />

                {/* Titre */}
                <div className="absolute top-2 left-3 text-sm font-medium text-green-700 bg-white/80 px-2 py-0.5 rounded-full shadow z-20">
                    🌾 La Ferme du Petit Essart
                    {simulationStatus.running && (
                        <span className="ml-2 text-green-500">● LIVE</span>
                    )}
                </div>

                {/* Tick counter */}
                {simulationStatus.running && (
                    <div className="absolute top-2 right-3 text-xs text-gray-500 bg-white/80 px-2 py-0.5 rounded-full shadow z-20">
                        Tick #{simulationStatus.tickCount}
                    </div>
                )}

                {/* Liens SVG animés */}
                {renderLiensSVG()}

                {/* Zones */}
                {zones.map(renderZone)}

                {/* Légende */}
                <div className="absolute bottom-2 left-2 bg-white/90 rounded-lg p-2 shadow text-xs z-20">
                    <div className="font-medium text-gray-700 mb-1">Équipements</div>
                    <div className="flex flex-wrap gap-1">
                        {Object.entries(TYPES_EQUIPEMENTS).slice(0, 6).map(([key, config]) => (
                            <span key={key} className="px-1 py-0.5 bg-gray-100 rounded text-[10px]" title={config.label}>
                                {config.icon}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Bouton Reset */}
                <button 
                    onClick={resetSimulation}
                    className="absolute bottom-2 right-2 bg-white/90 hover:bg-red-100 rounded-lg px-2 py-1 shadow text-xs text-gray-600 hover:text-red-600 z-20 transition-colors"
                >
                    🔄 Reset
                </button>
            </div>

            {/* Modal supervision */}
            {renderSupervisionModal()}
        </div>
    );
};

export default PlanMaraichage;